import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Message, Card } from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showSuccess,
  timestamp2string,
} from '../../helpers';
import { renderQuotaWithPrompt } from '../../helpers/render';
import useSmartBackV81 from '../../hooks/useSmartBackV81';
import useKeyModelOptionsV1013 from './useKeyModelOptionsV1013';
import useKeyCommercialOptionsV1110 from '../CommercialV106/useKeyCommercialOptionsV1110';
import KeyLimitsV1022 from './KeyLimitsV1022';
import { defaultRetrySettingsV1021 } from '../CommercialV106/retryPolicyV1021';
import {
  KeyCommercialEditorV106,
  KeyGroupEditorV107,
  KeyHelpV107,
} from '../CommercialV106/KeyCommercialEditorV106';

const EditToken = () => {
  const { t } = useTranslation();
  const params = useParams();
  const tokenId = params.id;
  const targetUserId = params.userId;
  const organizationId = params.organizationId;
  const isManaged = Boolean(targetUserId);
  const tokenApiBase = isManaged
    ? `/api/token/managed/${targetUserId}`
    : '/api/token';
  const returnPath = isManaged
    ? `/organization/${organizationId}/user/${targetUserId}`
    : '/token';
  const isEdit = tokenId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const identity = `${tokenApiBase}/${tokenId || 'new'}`;
  const [loadedIdentity, setLoadedIdentity] = useState(isEdit ? '' : identity);
  const originInputs = useMemo(
    () => ({
      name: '',
      remain_quota: isEdit ? 0 : 500000,
      expired_time: -1,
      unlimited_quota: false,
      models: [],
      model_scope: 'inherit',
      subnet: '',
      response_format: 'auto',
      rpm_limit: 0,
      tpm_limit: 0,
      ...(!isEdit
        ? {
            commercial_policy_v106: {
              strategy: 'quality',
              source_fallback_enabled: true,
              retry_enabled: true,
              retry_by_category: defaultRetrySettingsV1021(),
            },
          }
        : {}),
    }),
    [isEdit]
  );
  const [inputs, setInputs] = useState(originInputs);
  const [limitsOpen, setLimitsOpen] = useState({});
  const [invalidLimits, setInvalidLimits] = useState({});
  const [formRevision, setFormRevision] = useState(0);
  const { name, remain_quota, unlimited_quota } = inputs;
  const returnToTokens = useSmartBackV81(returnPath);
  const modelState = useKeyModelOptionsV1013(
    inputs.commercial_policy_v106,
    targetUserId,
    !loading && loadedIdentity === identity
  );
  const commercialOptions = useKeyCommercialOptionsV1110(targetUserId, !loading && loadedIdentity === identity);
  const manualSource = inputs.commercial_policy_v106?.strategy === 'manual';
  const modelOptions = useMemo(
    () =>
      modelState.models.map((value) => ({ key: value, text: value, value })),
    [modelState.models]
  );
  const unavailableSelections = modelState.ready
    ? (inputs.models || []).filter(
        (value) => !modelState.models.includes(value)
      )
    : [];
  const visibleModelOptions = useMemo(() => {
    if (manualSource || !modelState.ready) return modelOptions;
    const known = new Set(modelOptions.map((option) => option.value));
    const legacy = (inputs.models || [])
      .filter((model) => !known.has(model))
      .map((model) => ({
        key: `legacy:${model}`,
        text: `${model} (${t('token.edit.model_unavailable')})`,
        value: model,
      }));
    return [...modelOptions, ...legacy];
  }, [inputs.models, modelOptions, manualSource, modelState.ready, t]);
  const handleInputChange = (e, { name, value }) => {
    const section = {
      expired_time: 'expiration',
      rpm_limit: 'rate',
      tpm_limit: 'rate',
    }[name];
    if (section)
      setInvalidLimits((current) => ({ ...current, [section]: false }));
    setInputs((inputs) => ({
      ...inputs,
      [name]:
        name === 'models' && manualSource
          ? [...new Set([...unavailableSelections, ...value])]
          : value,
    }));
  };
  const handleCancel = () => {
    returnToTokens();
  };
  const setExpiredTime = (month, day, hour, minute) => {
    setInvalidLimits((current) => ({ ...current, expiration: false }));
    let now = new Date();
    let timestamp = now.getTime() / 1000;
    let seconds = month * 30 * 24 * 60 * 60;
    seconds += day * 24 * 60 * 60;
    seconds += hour * 60 * 60;
    seconds += minute * 60;
    if (seconds !== 0) {
      timestamp += seconds;
      setInputs({ ...inputs, expired_time: timestamp2string(timestamp) });
    } else {
      setInputs({ ...inputs, expired_time: -1 });
    }
  };

  const setUnlimitedQuota = () => {
    setInputs({ ...inputs, unlimited_quota: !unlimited_quota });
  };

  useEffect(() => {
    let active = true;
    setLimitsOpen({});
    setInvalidLimits({});
    if (!isEdit) {
      setInputs(originInputs);
      setLoadedIdentity(identity);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    setLoadedIdentity('');
    API.get(`${tokenApiBase}/${tokenId}`)
      .then((res) => {
        if (!active) return;
        const { success, message, data } = res.data || {};
        if (success && data) {
          if (data.expired_time !== -1) {
            data.expired_time = timestamp2string(data.expired_time);
          }
          if (!data.models) {
            data.models = [];
          } else {
            data.models = data.models.split(',');
          }
          data.model_scope =
            data.model_scope === 'custom' ||
            (data.model_scope !== 'inherit' && data.models.length)
              ? 'custom'
              : 'inherit';
          if (!data.response_format || data.response_format === 'inherit') {
            data.response_format = 'auto';
          }
          setInputs(data);
          setLoadedIdentity(identity);
        } else {
          showError(message || 'Failed to load token');
        }
      })
      .catch((error) => {
        if (active) showError(error.message || 'Network error');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isEdit, tokenApiBase, tokenId, identity, originInputs]);

  const submit = async () => {
    if (
      !modelState.ready ||
      modelState.models.length === 0 ||
      (inputs.model_scope === 'custom' && unavailableSelections.length)
    )
      return;
    if (!isEdit && inputs.name === '') return;
    const rpmLimit = Number(inputs.rpm_limit);
    const tpmLimit = Number(inputs.tpm_limit);
    if (
      !Number.isInteger(rpmLimit) ||
      rpmLimit < 0 ||
      !Number.isInteger(tpmLimit) ||
      tpmLimit < 0
    ) {
      showError(t('token.edit.messages.rate_limit_invalid'));
      setLimitsOpen((current) => ({ ...current, rate: true }));
      setInvalidLimits((current) => ({ ...current, rate: true }));
      requestAnimationFrame(() =>
        document.getElementById('key-rpm-v1022')?.focus()
      );
      return;
    }
    if (inputs.model_scope === 'custom' && !inputs.models.length) {
      showError(t('token.edit.messages.custom_models_required'));
      return;
    }
    if (
      inputs.commercial_policy_v106?.group_restriction_enabled &&
      !inputs.commercial_policy_v106.allowed_groups?.length
    ) {
      showError(
        t('token.edit.messages.groups_required', {
          defaultValue: 'Select at least one routing group',
        })
      );
      return;
    }
    let localInputs = {
      ...inputs,
      remain_quota: parseInt(inputs.remain_quota),
      rpm_limit: rpmLimit,
      tpm_limit: tpmLimit,
      models: inputs.model_scope === 'custom' ? [...inputs.models] : [],
    };
    if (localInputs.expired_time !== -1) {
      let time = Date.parse(localInputs.expired_time);
      if (isNaN(time)) {
        showError(t('token.edit.messages.expire_time_invalid'));
        setLimitsOpen((current) => ({ ...current, expiration: true }));
        setInvalidLimits((current) => ({ ...current, expiration: true }));
        requestAnimationFrame(() =>
          document.getElementById('key-expiration-v1022')?.focus()
        );
        return;
      }
      localInputs.expired_time = Math.ceil(time / 1000);
    }
    localInputs.models = localInputs.models.join(',');
    let res;
    if (isEdit) {
      res = await API.put(tokenApiBase, {
        ...localInputs,
        id: parseInt(tokenId),
      });
    } else {
      res = await API.post(tokenApiBase, localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('token.edit.messages.update_success'));
      } else {
        showSuccess(t('token.edit.messages.create_success'));
        setInputs(originInputs);
        setLimitsOpen({});
        setInvalidLimits({});
        setFormRevision((value) => value + 1);
      }
    } else {
      showError(message);
    }
  };

  if (inputs.managed_kind_v110 === 'coding_shared') {
    return (
      <div className='token-edit-page'>
        <Message icon='lock' header={t('token.coding_locked_v110', { defaultValue: 'Coding apps · Locked' })} />
        <Button icon='arrow left' onClick={handleCancel} aria-label={t('token.buttons.cancel', { defaultValue: 'Back' })} />
      </div>
    );
  }
  return (
    <div className='dashboard-container management-container-v4'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('token.edit.title_edit') : t('token.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Input
                label={t('token.edit.name')}
                name='name'
                placeholder={t('token.edit.name_placeholder')}
                onChange={handleInputChange}
                value={name}
                autoComplete='new-password'
                required={!isEdit}
              />
            </Form.Field>
            {commercialOptions.error && (
              <Message negative>
                {t('token.edit.options_load_error_v1110', { defaultValue: '无法加载 Key 分组与 Source 权限，请重试。 / Unable to load Key permissions. Please retry.' })}
                <Button type='button' size='small' icon='refresh' disabled={commercialOptions.loading}
                  aria-label={t('token.edit.source_scope_retry')}
                  onClick={commercialOptions.retry} />
              </Message>
            )}
            <KeyGroupEditorV107
              optionsState={commercialOptions}
              value={inputs.commercial_policy_v106}
              targetUserId={targetUserId}
              onChange={(value) =>
                setInputs((current) => ({
                  ...current,
                  commercial_policy_v106: value,
                }))
              }
            />
            <KeyCommercialEditorV106
              optionsState={commercialOptions}
              key={`${identity}:${formRevision}`}
              value={inputs.commercial_policy_v106}
              targetUserId={targetUserId}
              onChange={(value) =>
                setInputs((current) => ({
                  ...current,
                  commercial_policy_v106: value,
                }))
              }
            />
            <Form.Field>
              <label>{t('token.edit.model_scope')}</label>
              <Button.Group size='small'>
                <Button
                  type='button'
                  active={inputs.model_scope === 'inherit'}
                  onClick={() =>
                    setInputs((current) => ({
                      ...current,
                      model_scope: 'inherit',
                    }))
                  }
                >
                  {t('token.edit.model_scope_inherit')}
                </Button>
                <Button
                  type='button'
                  active={inputs.model_scope === 'custom'}
                  onClick={() =>
                    setInputs((current) => ({
                      ...current,
                      model_scope: 'custom',
                    }))
                  }
                >
                  {t('token.edit.model_scope_custom')}
                </Button>
              </Button.Group>
            </Form.Field>
            {modelState.missingSource && (
              <Message
                visible
                warning
                content={t('token.edit.source_scope_select')}
              />
            )}
            {modelState.error && (
              <Message visible negative>
                {t('token.edit.source_scope_error')}
                <Button
                  type='button'
                  size='small'
                  icon='refresh'
                  aria-label={t('token.edit.source_scope_retry')}
                  title={t('token.edit.source_scope_retry')}
                  onClick={modelState.retry}
                />
              </Message>
            )}
            {modelState.ready && modelState.models.length === 0 && (
              <Message
                visible
                negative
                className='key-strategy-unavailable-v1022'
                content={t('token.edit.strategy_unavailable_v1022', {
                  defaultValue:
                    '当前策略没有可用模型。请更换策略，或由管理员配置全参、无压缩能力及授权 Source。 / No models are available for this policy. Select another strategy or configure channel capabilities and authorized Sources.',
                })}
              />
            )}
            {modelState.ready && (
              <Message
                info
                content={t('token.edit.source_scope_count', {
                  count: modelState.models.length,
                })}
              />
            )}
            {inputs.model_scope === 'custom' ? (
              <>
                <Form.Field data-testid='model-scope-v1013'>
                  <Form.Dropdown
                    label={t('token.edit.models')}
                    placeholder={t('token.edit.models_placeholder')}
                    name='models'
                    fluid
                    multiple
                    search
                    onLabelClick={(e, { value }) => {
                      copy(value).then();
                    }}
                    selection
                    onChange={handleInputChange}
                    value={
                      manualSource
                        ? (inputs.models || []).filter((value) =>
                            modelState.models.includes(value)
                          )
                        : inputs.models
                    }
                    disabled={!modelState.ready}
                    loading={modelState.loading}
                    autoComplete='new-password'
                    options={visibleModelOptions}
                  />
                </Form.Field>
                {unavailableSelections.length > 0 && (
                  <Message visible warning className='key-invalid-models-v1013'>
                    <p>
                      {t('token.edit.source_scope_invalid', {
                        count: unavailableSelections.length,
                      })}
                    </p>
                    <p style={{ overflowWrap: 'anywhere' }}>
                      {unavailableSelections.join(', ')}
                    </p>
                    <Button
                      type='button'
                      size='small'
                      icon='trash alternate outline'
                      content={t('token.edit.source_scope_remove')}
                      onClick={() =>
                        setInputs((current) => ({
                          ...current,
                          models: current.models.filter((value) =>
                            modelState.models.includes(value)
                          ),
                        }))
                      }
                    />
                  </Message>
                )}
              </>
            ) : (
              !manualSource && (
                <Message info>{t('token.edit.model_scope_help')}</Message>
              )
            )}
            <Form.Field>
              <Form.Dropdown
                label={
                  <>
                    {t('token.edit.client_response_format', {
                      defaultValue: '客户端响应格式',
                    })}
                    <KeyHelpV107
                      label={t('token.edit.client_response_format_help_label', {
                        defaultValue: '响应格式说明',
                      })}
                      content={t('token.edit.client_response_format_help', {
                        defaultValue:
                          '只决定客户端收到的响应格式；不会改变上游输入协议或授予能力。自动模式按请求端点返回对应格式。',
                      })}
                    />
                  </>
                }
                name='response_format'
                fluid
                selection
                onChange={handleInputChange}
                value={inputs.response_format || 'auto'}
                options={[
                  {
                    key: 'auto',
                    text: t('token.edit.client_response_format_auto', {
                      defaultValue: '自动（按请求端点）',
                    }),
                    value: 'auto',
                  },
                  {
                    key: 'openai-chat',
                    text: 'OpenAI Chat Completions',
                    value: 'openai-chat',
                  },
                  {
                    key: 'openai-responses',
                    text: 'OpenAI Responses',
                    value: 'openai-responses',
                  },
                  {
                    key: 'anthropic',
                    text: 'Anthropic Messages',
                    value: 'anthropic',
                  },
                  { key: 'gemini', text: 'Gemini Content', value: 'gemini' },
                ]}
              />
            </Form.Field>
            <KeyLimitsV1022
              inputs={inputs}
              onChange={handleInputChange}
              setExpiredTime={setExpiredTime}
              open={limitsOpen}
              setOpen={setLimitsOpen}
              invalid={invalidLimits}
            />
            <Message>{t('token.edit.quota_notice')}</Message>
            <Form.Field>
              <Form.Input
                label={`${t('token.edit.quota')}${renderQuotaWithPrompt(
                  remain_quota,
                  t
                )}`}
                name='remain_quota'
                placeholder={t('token.edit.quota_placeholder')}
                onChange={handleInputChange}
                value={remain_quota}
                autoComplete='new-password'
                type='number'
                disabled={unlimited_quota}
              />
            </Form.Field>
            <Button
              type={'button'}
              onClick={() => {
                setUnlimitedQuota();
              }}
            >
              {unlimited_quota
                ? t('token.edit.buttons.cancel_unlimited')
                : t('token.edit.buttons.unlimited_quota')}
            </Button>
            <div className='key-submit-v1022'>
              <Button type='button' onClick={handleCancel}>
                {t('token.edit.buttons.cancel')}
              </Button>
              <Button
                type='button'
                positive
                disabled={
                  loading ||
                  loadedIdentity !== identity ||
                  !modelState.ready ||
                  modelState.models.length === 0 ||
                  (inputs.model_scope === 'custom' &&
                    unavailableSelections.length > 0)
                }
                onClick={submit}
              >
                {t('token.edit.buttons.submit')}
              </Button>
            </div>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditToken;
